import { useEffect, useState } from 'react';

/**
 * Tiny in-host navigation without react-router-dom.
 *
 * Modules each instantiate their own <BrowserRouter basename={basePath}>.
 * React Router v6 throws on nested routers, so the host deliberately has NO
 * Router; each module owns its own router for its own URL subtree
 * (/m/{module}/*).
 *
 * usePathname() must reflect changes from BOTH:
 *   - browser back/forward (popstate) — handled natively
 *   - host's sidebar/dashboard nav (custom 'host:navigate' event)
 *   - module-internal navigation (history.pushState via module's BrowserRouter)
 *     — pushState doesn't fire popstate, so we monkey-patch it once at first
 *     use to dispatch our own 'host:navigate' event. This lets the host's
 *     lastUrl-per-module tracking capture intra-module navigation, so
 *     clicking the sidebar tile again resumes at the URL the user left.
 */

const TEST_BASE_PATH = '/test';

export function getAppBasePath(): string {
  return window.location.pathname === TEST_BASE_PATH ||
    window.location.pathname.startsWith(`${TEST_BASE_PATH}/`)
    ? TEST_BASE_PATH
    : '';
}

export function stripAppBasePath(pathname: string): string {
  if (pathname === TEST_BASE_PATH) return '/';
  if (pathname.startsWith(`${TEST_BASE_PATH}/`)) {
    return pathname.slice(TEST_BASE_PATH.length) || '/';
  }
  return pathname || '/';
}

export function withAppBasePath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${getAppBasePath()}${normalized}`;
}

export function navigate(to: string) {
  const target = withAppBasePath(to);
  if (target === window.location.pathname) return;
  window.history.pushState({}, '', target);
  window.dispatchEvent(new Event('host:navigate'));
}

let pushStatePatched = false;
function patchHistoryPushState() {
  if (pushStatePatched) return;
  pushStatePatched = true;
  const origPush = window.history.pushState.bind(window.history);
  const origReplace = window.history.replaceState.bind(window.history);
  window.history.pushState = function (
    data: unknown,
    unused: string,
    url?: string | URL | null
  ) {
    origPush(data, unused, url);
    window.dispatchEvent(new Event('host:navigate'));
  };
  window.history.replaceState = function (
    data: unknown,
    unused: string,
    url?: string | URL | null
  ) {
    origReplace(data, unused, url);
    window.dispatchEvent(new Event('host:navigate'));
  };
}

export function usePathname(): string {
  const [pathname, setPathname] = useState(() => stripAppBasePath(window.location.pathname));
  useEffect(() => {
    patchHistoryPushState();
    const update = () => setPathname(stripAppBasePath(window.location.pathname));
    window.addEventListener('popstate', update);
    window.addEventListener('host:navigate', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('host:navigate', update);
    };
  }, []);
  return pathname;
}
